import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from "@coval/ui"
import { Copy, MoreHorizontal, Pin, PinOff, Settings } from "lucide-react"

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"
import { useQueryParams } from "@/hooks/useQueryParams"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import type { CoviChatItem } from "../model/types"
import { serializeTranscript } from "../utils/transcript"

export function SofiaOptionsMenu({
  orgSlug,
  transcript,
  pin,
  showSettings = true,
}: {
  orgSlug: string | null
  transcript?: {
    getItems: () => CoviChatItem[]
    disabled: boolean
  }
  pin?: {
    pinned: boolean
    onToggle: () => void
  }
  showSettings?: boolean
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [keyboardNavigation, setKeyboardNavigation] = useState(false)
  const [tooltipDismissed, setTooltipDismissed] = useState(false)
  const { copy } = useCopyToClipboard()
  const queryParams = useQueryParams()
  const router = useRouter()
  const organizationSlug = getRouteOrganizationSlug(router.asPath) ?? orgSlug
  const pointerFocusClass = keyboardNavigation
    ? undefined
    : "coval-custom-focus-ring outline-none"

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={(open) => {
        setTooltipDismissed(true)
        setTooltipOpen(false)
        setMenuOpen(open)
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          icon
          tooltip="Sofia Options"
          className={pointerFocusClass}
          tooltipProps={{
            open: !menuOpen && !tooltipDismissed && tooltipOpen,
            className: "data-[state=closed]:hidden",
            onOpenChange: setTooltipOpen,
            openOnInteraction: !menuOpen,
          }}
          onKeyDownCapture={() => {
            setKeyboardNavigation(true)
            setTooltipDismissed(false)
          }}
          onFocusCapture={(event) => {
            if (
              !tooltipDismissed &&
              event.currentTarget.matches(":focus-visible")
            ) {
              setKeyboardNavigation(true)
            }
          }}
          onPointerMove={() => {
            if (!menuOpen) {
              setTooltipDismissed(false)
            }
          }}
          onBlur={() => {
            if (!menuOpen) {
              setTooltipDismissed(false)
              setTooltipOpen(false)
            }
          }}
          onPointerDownCapture={() => setKeyboardNavigation(false)}
          disabled={!organizationSlug && !transcript && !pin}
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        aria-label="Sofia Options"
        className={pointerFocusClass}
        onPointerDownOutside={() => setKeyboardNavigation(false)}
        onKeyDownCapture={() => setKeyboardNavigation(true)}
        onPointerMoveCapture={() => setKeyboardNavigation(false)}
      >
        {transcript && (
          <DropdownMenuItem
            className={pointerFocusClass}
            disabled={transcript.disabled}
            onSelect={() => {
              void copy(serializeTranscript(transcript.getItems())).then(
                (copied) => {
                  if (copied) {
                    toast.success({ title: "Transcript copied" })
                  }
                },
              )
            }}
          >
            <Copy className="size-4" aria-hidden />
            Copy transcript
          </DropdownMenuItem>
        )}
        {pin && (
          <DropdownMenuItem
            className={pointerFocusClass}
            onSelect={pin.onToggle}
          >
            {pin.pinned ? (
              <PinOff className="size-4" aria-hidden />
            ) : (
              <Pin className="size-4" aria-hidden />
            )}
            {pin.pinned ? "Unpin chat" : "Pin chat"}
          </DropdownMenuItem>
        )}
        {showSettings && (
          <>
            {(transcript || pin) && <DropdownMenuSeparator />}
            {organizationSlug ? (
              <DropdownMenuItem asChild className={pointerFocusClass}>
                <Link
                  href={queryParams.getHref(
                    "/settings/sofia",
                    organizationSlug,
                  )}
                >
                  <Settings className="size-4" aria-hidden />
                  Sofia Settings
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled className={pointerFocusClass}>
                <Settings className="size-4" aria-hidden />
                Sofia Settings
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
